import React from 'react';
import { graphql, usePreloadedQuery, type EntryPointProps } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { PlaceholderUFOMigration as Placeholder } from '@atlassian/jira-placeholder/src/index.tsx';
import { ReportsOverviewSkeleton } from '@atlassian/jira-skeletons/src/ui/reports-overview/ReportsOverviewSkeleton.tsx';
import Breadcrumb from '@atlassian/jira-breadcrumbs/src/breadcrumb/component.tsx';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import { JIRA_PROJECT_TYPE_CORE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	isUserContext,
	isProjectContext,
} from '@atlassian/jira-providers-container-context/src/types.tsx';
import UserBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/user/view.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { srcReportsOverviewPageQuery } from '@atlassian/jira-relay/src/__generated__/srcReportsOverviewPageQuery.graphql';

import { PageLayout } from '@atlassian/jira-spa-apps-page-layout/src/page/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { ReportCategories as ReportCategoriesNew } from './ui/report-categories/index.tsx';
import { ReportCategoriesOld } from './ui/report-categories-old/index.tsx';
import messages from './messages.tsx';

export type Queries = { reportsOverviewPage: srcReportsOverviewPageQuery };

const analyticsAttributes = {
	type: 'reports',
};

const ReportCategories = componentWithFG(
	'hl-3120_redirect_for_reports_overview_page',
	ReportCategoriesNew,
	ReportCategoriesOld,
);

export const ReportsOverviewPage = ({
	queries: { reportsOverviewPage: queryReference },
}: EntryPointProps<Queries, {}, {}, {}>) => {
	const { formatMessage } = useIntl();
	const [{ data: containerContextData, error }] = useContainerContext();
	const data = usePreloadedQuery<srcReportsOverviewPageQuery>(
		graphql`
			query srcReportsOverviewPageQuery(
				$projectKey: String!
				$boardId: ID!
				$cloudId: ID!
				$isBoardContext: Boolean!
				$isMigrateTMPReportsOverviewEnabled: Boolean!
			) {
				...reportCategories_reportsOverviewPage
				...reportCategoriesOld_reportsOverviewPage
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectKey)
					@skip(if: $isBoardContext)
					@required(action: THROW) {
					projectType @required(action: THROW)
				}
			}
		`,
		queryReference,
	);

	const isBusinessProject =
		data.jira_projectByIdOrKey?.projectType === JIRA_PROJECT_TYPE_CORE_PROJECT;

	if (!containerContextData || error || !queryReference) {
		return null;
	}

	const projectBreadcrumb = isProjectContext(containerContextData) ? (
		<ProjectBreadcrumb
			projectKey={containerContextData.project.key}
			projectName={containerContextData.project.name}
			projectId={containerContextData.project.id}
			isSimplifiedProject={containerContextData.project.isSimplified}
		>
			<BreadcrumbRenderer />
		</ProjectBreadcrumb>
	) : undefined;

	const userBreadcrumb = isUserContext(containerContextData) ? (
		<UserBreadcrumb
			profileUrl={containerContextData.user.profileUrl}
			name={containerContextData.user.name}
		>
			<Breadcrumb
				breadcrumb={{
					text: containerContextData.scope.name ?? '',
					href: `/jira/people/${containerContextData.user.accountId}/boards/${containerContextData.scope.id}`,
					type: 'boardName',
				}}
			/>
			<BreadcrumbRenderer />
		</UserBreadcrumb>
	) : undefined;

	const breadcrumb = isProjectContext(containerContextData) ? projectBreadcrumb : userBreadcrumb;

	return (
		<UFOSegment name="jira-reports-overview-page">
			<Box xcss={wrapperStyles}>
				<AppBase id="jira-summary">
					<DocumentTitle title={formatMessage(messages.documentTitle)} />
					<PageLayout>
						<Placeholder name="reports-overview-page" fallback={<ReportsOverviewSkeleton />}>
							<SubProductUpdater subProduct="software" />
							{!getWillShowNav4() && !isBusinessProject && (
								<PageHeader truncateTitle breadcrumbs={breadcrumb}>
									{formatMessage(messages.pageHeader)}
								</PageHeader>
							)}
							<JSErrorBoundary
								id="reportsOverviewPage"
								packageName="jiraReportsOverviewPage"
								teamName="helios-headcoach"
							>
								<ContextualAnalyticsData sourceName="allReports" sourceType={SCREEN}>
									{getWillShowNav4() || isBusinessProject ? (
										<Box paddingBlockStart="space.300">
											<ReportCategories reportCategoriesData={data} />
										</Box>
									) : (
										<ReportCategories reportCategoriesData={data} />
									)}
									<FireScreenAnalytics attributes={analyticsAttributes} />
								</ContextualAnalyticsData>
							</JSErrorBoundary>
						</Placeholder>
					</PageLayout>
				</AppBase>
			</Box>
		</UFOSegment>
	);
};

const wrapperStyles = xcss({
	overflow: 'auto',
	height: '100%',
});
